import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Cpu, Truck, Package } from "lucide-react";

/** EVERY ENGINE CARRYING THE VALUE YOU TYPED — his whole point, 2026-10-16.
 *
 *  *"Someone looking for a Cummins engine types a CPL # — e.g. 4908 — and gets EVERY engine we
 *  have with that CPL: engines still in a truck AND engines already pulled as parts, each engine
 *  listed ONCE, even if both its plates carry the same value, showing which plate the match came
 *  from."*
 *
 *  The shelf answers first (the parts list above this). This band is the rest of the answer: the
 *  engines still bolted into a truck, and the pulled ones, named by their serial with the plate
 *  that carried the match. */
export function DataplateMatchBand({ q, testid = "dataplate-band" }) {
  const [engines, setEngines] = useState([]);
  const term = (q || "").trim();

  useEffect(() => {
    if (term.length < 3) { setEngines([]); return; }
    let live = true;
    api.get("/dataplates/search", { params: { q: term } })
      .then(({ data }) => live && setEngines(data.engines || []))
      .catch(() => live && setEngines([]));
    return () => { live = false; };
  }, [term]);

  if (!engines.length) return null;
  return (
    <div data-testid={testid} className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white">
        <Cpu className="w-4 h-4" />
        <span className="font-display text-sm font-bold">
          {engines.length} engine{engines.length === 1 ? "" : "s"} with “{term}” on the maker's dataplate
        </span>
        <span className="ml-auto text-[10px] font-mono text-slate-300">read off the PDF — exact</span>
      </div>
      <div className="divide-y divide-slate-100">
        {engines.map((e) => {
          const where = e.where;
          const to = where?.kind === "part" ? `/parts/${where.id}` : where?.kind === "truck" ? `/donors/${where.id}` : null;
          const Icon = where?.kind === "part" ? Package : Truck;
          const seen = new Set();
          const matches = (e.matched_on || []).filter((m) => {
            const k = `${m.label}::${m.value}::${m.plate}`;
            if (seen.has(k)) return false;
            seen.add(k); return true;
          });
          return (
            <div key={e.serial} data-testid={`${testid}-engine-${e.serial}`} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="rounded-md bg-slate-800 text-white text-[9px] uppercase font-mono">ESN {e.serial}</Badge>
                {where ? (
                  <Link to={to} data-testid={`${testid}-link-${e.serial}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-800 hover:text-[#B01E28]">
                    <Icon className="w-3.5 h-3.5 text-slate-400" />
                    {where.name || where.code}
                    <span className="font-mono text-[11px] text-slate-400">{where.code}</span>
                  </Link>
                ) : <span className="text-sm text-slate-400 italic">not linked to a truck or a part</span>}
                {where && <span data-testid={`${testid}-where-${e.serial}`} className="text-[11px] text-slate-500">· {where.words}</span>}
              </div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                {matches.map((m, i) => (
                  <span key={i} data-testid={`${testid}-match-${e.serial}-${i}`} className="text-[11px] text-slate-600">
                    <b className="font-mono text-slate-800">{m.value}</b>
                    <span className="text-slate-500"> is the {m.label}</span>
                    <span className="text-slate-400"> · off the {m.plate} plate</span>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
